import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import { StripeReturnPollingTimeoutError } from "./refetchStripeSubscriptionReturn";

/**
 * Toast handler for the `onPollingTimeout` callback of useStripeCheckoutReturn.
 * Wires the documented "still confirming, refresh in a minute" guidance so
 * users don't sit on a frozen banner past the 24s polling window — without
 * this, a slow webhook leaves the UI silent and users retry, creating
 * duplicate charges.
 */
export function useStripeCheckoutReturnPollingTimeoutToast(): (
  returnParams: URLSearchParams,
) => void {
  const createToast = useToast();
  return useCallback(() => {
    if (createToast.isActive("stripe-return-polling-timeout")) return;
    createToast({
      id: "stripe-return-polling-timeout",
      status: "warning",
      title: "Still confirming your purchase",
      description:
        "This is taking longer than usual. Please refresh in a minute — if the issue persists, contact support.",
      duration: null,
      isClosable: true,
    });
  }, [createToast]);
}

type StripeCheckoutReturnOptions = {
  expectedFlow: "portal";
  onSuccessReturn: (returnParams: URLSearchParams) => Promise<unknown> | void;
  onCancelReturn?: (returnParams: URLSearchParams) => Promise<unknown> | void;
  /**
   * Called when refetchStripeReturnUntil exhausts its retries waiting for the
   * webhook to mirror the purchase. Callers should use this to surface a
   * user-visible "still confirming" state — without it, the modal currently
   * exits silently and the user may retry, causing duplicate charges.
   */
  onPollingTimeout?: (returnParams: URLSearchParams) => void;
};

/**
 * Despite the "Checkout" in the name, this hook now only handles return URLs from
 * the Stripe Billing Customer Portal (the embedded payment flows complete inline
 * and never round-trip through Stripe-hosted UI). Kept under the original name
 * because `stripe_flow`/`stripe_result` URL params are still the contract.
 */
export function useStripeCheckoutReturn({
  expectedFlow,
  onSuccessReturn,
  onCancelReturn,
  onPollingTimeout,
}: StripeCheckoutReturnOptions) {
  const [searchParams, setSearchParams] = useSearchParams();
  const handledKey = useRef<string>();
  const onSuccessRef = useRef(onSuccessReturn);
  const onCancelRef = useRef(onCancelReturn);
  const onPollingTimeoutRef = useRef(onPollingTimeout);
  onSuccessRef.current = onSuccessReturn;
  onCancelRef.current = onCancelReturn;
  onPollingTimeoutRef.current = onPollingTimeout;

  useEffect(() => {
    const flow = searchParams.get("stripe_flow");
    const result = searchParams.get("stripe_result");
    if (!flow || !result || flow !== expectedFlow) {
      // Reset the latch once the Stripe params are gone so later identical returns
      // in the same SPA session are still handled.
      handledKey.current = undefined;
      return;
    }

    const currentKey = `${flow}:${result}`;
    if (handledKey.current === currentKey) {
      return;
    }
    handledKey.current = currentKey;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("stripe_flow");
    nextParams.delete("stripe_result");
    // Keep the original params for callbacks before the hook removes them from the URL.
    const returnParams = new URLSearchParams(searchParams);

    const handleReturn = async () => {
      try {
        if (result === "success" || result === "returned") {
          await onSuccessRef.current(returnParams);
        } else if (result === "cancel") {
          await onCancelRef.current?.(returnParams);
        }
        setSearchParams(nextParams, { replace: true });
      } catch (error) {
        if (error instanceof StripeReturnPollingTimeoutError) {
          console.warn(
            "[useStripeCheckoutReturn] Stripe return state is still pending; preserving query params for retry.",
            error,
          );
          handledKey.current = undefined;
          // Let the caller surface a user-visible "we're still confirming"
          // state. Without this hook, the modal exits silently and users
          // often retry, creating duplicate charges.
          onPollingTimeoutRef.current?.(returnParams);
          return;
        }

        console.error(
          "[useStripeCheckoutReturn] Error handling Stripe return:",
          error,
        );
        handledKey.current = undefined;
        // Successful returns are server-backed and should still clear the URL.
        // Cancel returns need the params preserved so lock-release cleanup can retry.
        if (result === "success" || result === "returned") {
          setSearchParams(nextParams, { replace: true });
        }
      }
    };

    void handleReturn();
  }, [expectedFlow, searchParams, setSearchParams]);
}
